import React, {useEffect, useState} from 'react';
import {useEditFundraiserFinanceForm} from '../../model';
import {ActionModal, ErrorModal, FormInput, useErrorHandler} from '../../../../shared';
import {FundraiserFinanceModel} from '../../../../entities';
import {DatePicker} from '@gravity-ui/date-components';
import {DateTime} from '@gravity-ui/date-utils';
import {Text} from '@gravity-ui/uikit';
import styles from './EditFundraiserFinanceModal.module.scss';

interface EditFundraiserFinanceModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  fundraiserId: string;
  financeData?: FundraiserFinanceModel;
}

const FORMAT = 'DD.MM.YYYY';

// Функция для форматирования числа с разделителями разрядов
const formatNumberWithSpaces = (value: string | number): string => {
  if (!value) return '';
  const numStr = value.toString().replace(/\s/g, '');
  return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Функция для удаления разделителей и получения чистого числа
const parseFormattedNumber = (value: string): string => {
  return value.replace(/\s/g, '');
};

export const EditFundraiserFinanceModal = ({
  open,
  setOpen,
  fundraiserId,
  financeData,
}: EditFundraiserFinanceModalProps) => {
  const [isCommissionConfirmModalOpen, setIsCommissionConfirmModalOpen] = useState(false);
  const [initialCommissionPercentage, setInitialCommissionPercentage] = useState<string>('');

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Финансовая информация',
    title: 'Ошибка при редактировании финансовой информации',
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    isLoading,
    submitError,
    resetForm,
    submitForm,
  } = useEditFundraiserFinanceForm({setOpen, fundraiserId, financeData});

  // Сохраняем изначальное значение комиссии при открытии модалки
  useEffect(() => {
    if (open && financeData?.commissionPercentage !== undefined) {
      setInitialCommissionPercentage(financeData.commissionPercentage.toString());
    }
  }, [open, financeData?.commissionPercentage]);

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  const handleDateChange = (date: DateTime | null) => {
    setFieldValue('end', date);
  };

  // Обработчик изменения для поля requiredAmount
  const handleRequiredAmountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const rawValue = parseFormattedNumber(e.target.value);
    setFieldValue('requiredAmount', rawValue);
  };

  // Проверка, изменилась ли комиссия
  const isCommissionChanged = () => {
    return initialCommissionPercentage !== values.commissionPercentage;
  };

  // Обработчик клика на "Сохранить"
  const handleSaveClick = () => {
    if (isCommissionChanged()) {
      setIsCommissionConfirmModalOpen(true);
    } else {
      submitForm();
    }
  };

  // Подтверждение изменения комиссии
  const handleConfirmCommissionChange = () => {
    setIsCommissionConfirmModalOpen(false);
    submitForm();
  };

  // Отмена изменения комиссии
  const handleCancelCommissionChange = () => {
    setIsCommissionConfirmModalOpen(false);
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={handleSaveClick}
        isLoading={isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Редактирование'}
        textButtonApply={'Сохранить'}
        textButtonCancel={'Отменить'}>
        <FormInput
          label="Сумма (требуемая)"
          placeholder="Введите требуемую сумму"
          name="requiredAmount"
          value={formatNumberWithSpaces(values.requiredAmount)}
          onChange={handleRequiredAmountChange}
          onBlur={handleBlur}
          type="text"
          error={touched.requiredAmount && errors.requiredAmount}
        />

        <FormInput
          label="Комиссия проекта"
          placeholder="Введите процент комиссии"
          name="commissionPercentage"
          value={values.commissionPercentage}
          onChange={handleChange}
          onBlur={handleBlur}
          type="number"
          isRequired={true}
          error={touched.commissionPercentage && errors.commissionPercentage}
        />

        <div className={styles.field}>
          <Text variant="subheader-1" className={styles.label}>
            Дата завершения сбора
          </Text>
          <DatePicker
            className={styles.datePicker}
            onUpdate={handleDateChange}
            size="xl"
            value={values.end}
            placeholder="Выберите дату"
            format={FORMAT}
            hasClear
          />
          {touched.end && errors.end && (
            <Text variant="body-1" color="danger">
              {errors.end}
            </Text>
          )}
        </div>
      </ActionModal>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />

      <ActionModal
        open={isCommissionConfirmModalOpen}
        onClose={handleCancelCommissionChange}
        setOpen={setIsCommissionConfirmModalOpen}
        resetForm={() => {}}
        submitForm={handleConfirmCommissionChange}
        isLoading={false}
        size={'s'}
        buttonsSize={'l'}
        caption={'Подтверждение'}
        textButtonApply={'Да'}
        propsButtonApply={{view: 'normal'}}
        propsButtonCancel={{view: 'flat-secondary'}}
        textButtonCancel={'Отменить'}>
        <Text variant="body-2">Вы уверены, что хотите изменить комиссию проекта?</Text>
      </ActionModal>
    </>
  );
};
